<template>
  <div class="cobranca-page-wrapper">
    <!-- Header com Título da Tela e Barra de Filtros Responsiva -->
    <div class="cobranca-top-toolbar mb-4">
      <div class="cobranca-title-container">
        <h4 class="cobranca-page-heading">Cobrança</h4>
      </div>

      <div class="cobranca-filters-container">
        <!-- Filtro Genérico de Período e Datas -->
        <div class="filtro-toolbar-item">
          <label class="filtro-toolbar-label">Período e Vencimento</label>
          <FiltroPeriodoBadges
            :data-de="filtro.dataDe"
            :data-ate="filtro.dataAte"
            :periodo-ativo="filtro.periodo"
            :incluir-inputs-data="true"
            :ate-ontem="true"
            :sync-url="false"
            @update:dataDe="filtro.dataDe = $event"
            @update:dataAte="filtro.dataAte = $event"
            @update:periodoAtivo="filtro.periodo = $event"
            @change="onPeriodoChange"
          />
        </div>

        <!-- Filtro Tipo Pessoa -->
        <FiltroDropdownGenerico
          label="Tipo Pessoa"
          v-model="filtro.tipo_pessoa"
          :options="tipo_pessoa"
          value-key="value"
          label-key="nome"
          class="filtro-responsive-item"
          width="120px"
          @change="onFiltroChange"
        />

        <!-- Filtro Forma de Pagamento -->
        <FiltroDropdownGenerico
          label="Forma de pagamento"
          v-model="filtro.forma_pagamento"
          :options="formasPagamento || []"
          value-key="id"
          label-key="nome"
          class="filtro-responsive-item"
          width="145px"
          @change="onFiltroChange"
        />

        <!-- Filtro Empresa -->
        <FiltroDropdownGenerico
          label="Empresa"
          v-model="filtro.id_empresa"
          :options="empresas || []"
          value-key="id"
          label-key="razao_social"
          class="filtro-responsive-item"
          width="140px"
          @change="onFiltroChange"
        />

        <!-- Filtro Status / Quitação -->
        <FiltroDropdownGenerico
          label="Status / Quitação"
          v-model="filtro.status_conta"
          :options="status_conta_opcoes"
          value-key="value"
          label-key="nome"
          class="filtro-responsive-item"
          width="165px"
          @change="onFiltroChange"
        />

        <!-- Botão Filtrar Alinhado Perfeitamente à Base dos Inputs -->
        <div class="filtro-toolbar-btn-wrapper">
          <button
            type="button"
            class="filtro-btn-action"
            @click="close()"
            title="Aplicar filtros de cobrança"
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon>
            </svg>
            <span>Filtrar</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Colunas de Cobrança (Responsivas) -->
    <vs-row vs-type="flex" vs-justify="flex-start">
      <!-- 1. Coluna A Cobrar -->
      <vs-col vs-lg="4" vs-md="12" vs-sm="12" vs-xs="12" vs-w="4" class="px-2 mb-3">
        <div class="cobranca-kanban-column">
          <!-- Header Genérico da Coluna Kanban -->
          <AppKanbanColumnHeader
            title="A Cobrar"
            :count="paraCobrar.length"
            icon="clock"
            variant="slate"
          >
            <template #bottom>
              <div class="cobranca-col-meta" title="Último disparo em massa">
                <AppIcon name="clock" size="12" />
                <span>{{ ultimoJob ? formatarDataJob(ultimoJob.createdAt) : 'Sem disparos' }}</span>
              </div>
            </template>
            <template #actions>
              <button
                type="button"
                class="cobranca-btn-disparo"
                style="background: #334155; margin-right: 4px;"
                @click="abrirReguaDisparos()"
                title="Configurar Régua de Cobrança WATI (Abre em Nova Aba)"
              >
                <AppIcon name="settings" size="12" />
                <span>Régua WATI</span>
              </button>
              <button
                type="button"
                class="cobranca-btn-disparo"
                @click="abrirModalMassa()"
                :disabled="paraCobrar.length === 0"
                title="Abrir disparo de cobrança em massa via WATI"
              >
                <AppIcon name="send" size="12" />
                <span>Disparo em Massa</span>
              </button>
            </template>
          </AppKanbanColumnHeader>

          <perfect-scrollbar
            class="scroll-cobranca"
            @ps-y-reach-end="showMoreCobrancaParaCobrar()"
          >
            <div id="paraCobrar" class="vs-con-loading__container">
              <!-- 1. Skeleton Loading Shimmer Genérico -->
              <AppSkeletonList v-if="loadingParaCobrar" :count="3" />

              <!-- 2. Empty State Genérico -->
              <AppEmptyState
                v-else-if="!paraCobrarLista.length"
                variant="pendente"
                icon="inbox"
                title="Nenhuma Fatura a Cobrar"
                description="Não foram encontradas faturas pendentes de cobrança para os filtros aplicados."
              />

              <!-- 3. Lista de Cards -->
              <template v-else>
                <div v-for="data in paraCobrarLista" :key="data.id" class="px-2 my-2">
                  <div class="cobranca-apple-card" :class="{ 'card-vencido': Number(data.dias_atraso != null ? data.dias_atraso : data.diasVencidos) > 0 }">
                    <!-- Header do Card: Cliente + Badges alinhados -->
                    <div class="cobranca-card-header">
                      <div class="cobranca-cliente-wrapper" title="Clique no nome para abrir o cadastro completo do cliente">
                        <VisualizarCliente
                          @update="close()"
                          :id_cliente="data.id_cliente"
                          :nomeCliente="data.nomeCliente"
                        />
                      </div>
                      <div class="cobranca-badges-group">
                        <!-- Status Badge -->
                        <span 
                          class="apple-card-badge cursor-pointer" 
                          :class="getStatusBadge(data).class"
                          :title="getStatusBadge(data).tooltip"
                        >
                          <AppIcon v-if="Number(data.dias_atraso != null ? data.dias_atraso : data.diasVencidos) > 0" name="clock" size="12" class="badge-svg-icon" />
                          <AppIcon v-else name="check" size="12" class="badge-svg-icon" />
                          {{ getStatusBadge(data).text }}
                        </span>

                        <!-- Forma de Pagamento Badge -->
                        <span 
                          class="apple-card-badge cursor-pointer" 
                          :class="getFormaPagamentoBadge(data).class"
                          :title="getFormaPagamentoBadge(data).title"
                        >
                          <AppIcon :name="getFormaPagamentoBadge(data).icon" size="12" class="badge-svg-icon" />
                          {{ getFormaPagamentoBadge(data).text }}
                        </span>

                        <!-- Dias Vencimento Badge -->
                        <span 
                          class="apple-card-badge cursor-pointer" 
                          :class="getDiasVencimentoBadge(data).class"
                          :title="getDiasVencimentoBadge(data).tooltip"
                        >
                          <AppIcon name="calendar" size="12" class="badge-svg-icon" />
                          {{ getDiasVencimentoBadge(data).text }}
                        </span>
                      </div>
                    </div>

                    <!-- Corpo Interativo: Detalhes, Pills de Documentos e Linha Financeira -->
                    <div 
                      class="cobranca-card-body" 
                      @click="(contaSelecionada = data), (iniciarModal = true)"
                      title="Clique para abrir detalhes e opções de cobrança"
                    >
                      <!-- Pills de Documentos com Links para nova aba -->
                      <div class="cobranca-docs-row">
                        <span
                          class="cobranca-doc-pill doc-link"
                          v-if="data.numero || data.id_nota"
                          @click.stop="abrirLinkNota(data)"
                          title="Abrir Nota Fiscal em nova aba"
                        >
                          <AppIcon name="file" size="11" />
                          Nota <b>#{{ data.numero || data.id_nota }}</b>
                          <svg class="doc-link-arrow" width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="7" y1="17" x2="17" y2="7"></line><polyline points="7 7 17 7 17 17"></polyline></svg>
                        </span>
                        <span
                          class="cobranca-doc-pill doc-link"
                          @click.stop="abrirLinkConta(data)"
                          title="Abrir Contas a Receber em nova aba"
                        >
                          <AppIcon name="card" size="11" />
                          Conta <b>#{{ data.id }}</b>
                          <svg class="doc-link-arrow" width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="7" y1="17" x2="17" y2="7"></line><polyline points="7 7 17 7 17 17"></polyline></svg>
                        </span>
                        <span
                          class="cobranca-doc-pill doc-contrato doc-link"
                          v-if="data.id_contrato"
                          @click.stop="abrirLinkContrato(data)"
                          title="Abrir Contrato em nova aba"
                        >
                          <AppIcon name="file" size="11" />
                          Contrato <b>#{{ data.id_contrato }}</b>
                          <svg class="doc-link-arrow" width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="7" y1="17" x2="17" y2="7"></line><polyline points="7 7 17 7 17 17"></polyline></svg>
                        </span>
                      </div>

                      <!-- Linha Financeira: Valor e Vencimento -->
                      <div class="cobranca-financeiro-row">
                        <div class="cobranca-valor-container">
                          <span class="cobranca-valor-label">VALOR</span>
                          <span class="cobranca-valor-numero">{{ $currency(data.valor) }}</span>
                        </div>
                        <div class="cobranca-vencimento-container">
                          <span class="cobranca-vencimento-label">VENCIMENTO</span>
                          <span class="cobranca-vencimento-data">
                            <AppIcon name="calendar" size="12" />
                            {{ data.vencimento }}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </template>
            </div>
          </perfect-scrollbar>
        </div>
      </vs-col>
      <!-- 2. Coluna Cobranças Realizadas -->
      <vs-col vs-lg="4" vs-md="12" vs-sm="12" vs-xs="12" vs-w="4" class="px-2 mb-3">
        <div class="cobranca-kanban-column">
          <!-- Header Genérico da Coluna Kanban -->
          <AppKanbanColumnHeader
            title="Cobranças Realizadas"
            :count="sucedidos.length"
            icon="check"
            variant="emerald"
            subtitle="Contatos e mensagens entregues"
          />

          <perfect-scrollbar
            class="scroll-cobranca"
            @ps-y-reach-end="showMoreCobrancaSucedidos()"
          >
            <div id="sucedidos" class="vs-con-loading__container">
              <!-- 1. Skeleton Loading Shimmer Genérico -->
              <AppSkeletonList v-if="loadingSucedidos" :count="3" />

              <!-- 2. Empty State Genérico -->
              <AppEmptyState
                v-else-if="!sucedidosLista.length"
                variant="sucesso"
                icon="send"
                title="Sem Cobranças Realizadas"
                description="Nenhuma cobrança ou disparo foi registrado como concluído no período selecionado."
              />

              <!-- 3. Lista de Cards -->
              <template v-else>
                <div v-for="data in sucedidosLista" :key="data.id" class="px-2 my-2">
                  <div class="cobranca-apple-card card-sucedido">
                    <!-- Header do Card: Cliente + Badges alinhados -->
                    <div class="cobranca-card-header">
                      <div class="cobranca-cliente-wrapper" title="Clique no nome para abrir o cadastro completo do cliente">
                        <VisualizarCliente
                          @update="close()"
                          :id_cliente="data.id_cliente"
                          :nomeCliente="data.nomeCliente"
                        />
                      </div>
                      <div class="cobranca-badges-group">
                        <!-- Status Badge -->
                        <span 
                          class="apple-card-badge cursor-pointer badge-pago" 
                          title="Cobrança realizada com sucesso no período"
                        >
                          <AppIcon name="check" size="12" class="badge-svg-icon" />
                          Cobrada
                        </span>

                        <!-- Forma de Pagamento Badge -->
                        <span 
                          class="apple-card-badge cursor-pointer" 
                          :class="getFormaPagamentoBadge(data).class"
                          :title="getFormaPagamentoBadge(data).title"
                        >
                          <AppIcon :name="getFormaPagamentoBadge(data).icon" size="12" class="badge-svg-icon" />
                          {{ getFormaPagamentoBadge(data).text }}
                        </span>
                      </div>
                    </div>

                    <!-- Corpo: Detalhes, Pills de Documentos e Linha Financeira -->
                    <div class="cobranca-card-body">
                      <!-- Pills de Documentos com Links para nova aba -->
                      <div class="cobranca-docs-row">
                        <span
                          class="cobranca-doc-pill doc-link"
                          v-if="data.numero || data.id_nota"
                          @click.stop="abrirLinkNota(data)"
                          title="Abrir Nota Fiscal em nova aba"
                        >
                          <AppIcon name="file" size="11" />
                          Nota <b>#{{ data.numero || data.id_nota }}</b>
                          <svg class="doc-link-arrow" width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="7" y1="17" x2="17" y2="7"></line><polyline points="7 7 17 7 17 17"></polyline></svg>
                        </span>
                        <span
                          class="cobranca-doc-pill doc-link"
                          @click.stop="abrirLinkConta(data)"
                          title="Abrir Contas a Receber em nova aba"
                        >
                          <AppIcon name="card" size="11" />
                          Conta <b>#{{ data.id }}</b>
                          <svg class="doc-link-arrow" width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="7" y1="17" x2="17" y2="7"></line><polyline points="7 7 17 7 17 17"></polyline></svg>
                        </span>
                        <span
                          class="cobranca-doc-pill doc-contrato doc-link"
                          v-if="data.id_contrato"
                          @click.stop="abrirLinkContrato(data)"
                          title="Abrir Contrato em nova aba"
                        >
                          <AppIcon name="file" size="11" />
                          Contrato <b>#{{ data.id_contrato }}</b>
                          <svg class="doc-link-arrow" width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="7" y1="17" x2="17" y2="7"></line><polyline points="7 7 17 7 17 17"></polyline></svg>
                        </span>
                      </div>

                      <!-- Linha Financeira: Valor e Vencimento -->
                      <div class="cobranca-financeiro-row">
                        <div class="cobranca-valor-container">
                          <span class="cobranca-valor-label">VALOR</span>
                          <span class="cobranca-valor-numero text-emerald-700">{{ $currency(data.valor) }}</span>
                        </div>
                        <div class="cobranca-vencimento-container">
                          <span class="cobranca-vencimento-label">VENCIMENTO</span>
                          <span class="cobranca-vencimento-data">
                            <AppIcon name="calendar" size="12" />
                            {{ data.vencimento }}
                          </span>
                        </div>
                      </div>

                      <!-- Observação / Motivo -->
                      <div v-if="data.motivo" class="cobranca-obs-row" title="Observação de cobrança">
                        <span class="cobranca-obs-label">Obs:</span>
                        <span class="cobranca-obs-text">{{ data.motivo }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </template>
            </div>
          </perfect-scrollbar>
        </div>
      </vs-col>
      <!-- 3. Coluna Não Realizadas / Falhas -->
      <vs-col vs-lg="4" vs-md="12" vs-sm="12" vs-xs="12" vs-w="4" class="px-2 mb-3">
        <div class="cobranca-kanban-column">
          <!-- Header Genérico da Coluna Kanban -->
          <AppKanbanColumnHeader
            title="Não Realizadas / Não sucedidas"
            :count="naoSucedidos.length"
            icon="error"
            variant="rose"
            subtitle="Contas declinadas, suspensas ou com erro"
          />

          <perfect-scrollbar
            class="scroll-cobranca"
            @ps-y-reach-end="showMoreCobrancaNaoSucedidos()"
          >
            <div id="naoSucedidos" class="vs-con-loading__container">
              <!-- 1. Skeleton Loading Shimmer Genérico -->
              <AppSkeletonList v-if="loadingNaoSucedidos" :count="3" />

              <!-- 2. Empty State Genérico -->
              <AppEmptyState
                v-else-if="!naoSucedidosLista.length"
                variant="success"
                icon="sparkles"
                title="Tudo em Dia!"
                description="Excelente! Não constam cobranças com erro ou falhas de envio registradas no período."
              />

              <!-- 3. Lista de Cards -->
              <template v-else>
                <div
                  v-for="data in naoSucedidosLista"
                  :key="data.id"
                  class="px-2 my-2"
                >
                  <div class="cobranca-apple-card card-nao-sucedido">
                    <!-- Header do Card: Cliente + Badges alinhados -->
                    <div class="cobranca-card-header">
                      <div class="cobranca-cliente-wrapper" title="Clique no nome para abrir o cadastro completo do cliente">
                        <VisualizarCliente
                          @update="close()"
                          :id_cliente="data.id_cliente"
                          :nomeCliente="data.nomeCliente"
                        />
                      </div>
                      <div class="cobranca-badges-group">
                        <!-- Status Badge -->
                        <span 
                          class="apple-card-badge cursor-pointer badge-falha" 
                          title="Disparo não sucedido ou pendência no envio"
                        >
                          <AppIcon name="close" size="12" class="badge-svg-icon" />
                          Não Sucedido
                        </span>

                        <!-- Forma de Pagamento Badge -->
                        <span 
                          class="apple-card-badge cursor-pointer" 
                          :class="getFormaPagamentoBadge(data).class"
                          :title="getFormaPagamentoBadge(data).title"
                        >
                          <AppIcon :name="getFormaPagamentoBadge(data).icon" size="12" class="badge-svg-icon" />
                          {{ getFormaPagamentoBadge(data).text }}
                        </span>
                      </div>
                    </div>

                    <!-- Corpo: Detalhes, Pills de Documentos e Linha Financeira -->
                    <div class="cobranca-card-body">
                      <!-- Pills de Documentos com Links para nova aba -->
                      <div class="cobranca-docs-row">
                        <span
                          class="cobranca-doc-pill doc-link"
                          v-if="data.id_nota || data.numero"
                          @click.stop="abrirLinkNota(data)"
                          title="Abrir Nota Fiscal em nova aba"
                        >
                          <AppIcon name="file" size="11" />
                          Nota <b>#{{ data.id_nota || data.numero }}</b>
                          <svg class="doc-link-arrow" width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="7" y1="17" x2="17" y2="7"></line><polyline points="7 7 17 7 17 17"></polyline></svg>
                        </span>
                        <span
                          class="cobranca-doc-pill doc-link"
                          @click.stop="abrirLinkConta(data)"
                          title="Abrir Contas a Receber em nova aba"
                        >
                          <AppIcon name="card" size="11" />
                          Conta <b>#{{ data.id }}</b>
                          <svg class="doc-link-arrow" width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="7" y1="17" x2="17" y2="7"></line><polyline points="7 7 17 7 17 17"></polyline></svg>
                        </span>
                        <span
                          class="cobranca-doc-pill doc-contrato doc-link"
                          v-if="data.id_contrato"
                          @click.stop="abrirLinkContrato(data)"
                          title="Abrir Contrato em nova aba"
                        >
                          <AppIcon name="file" size="11" />
                          Contrato <b>#{{ data.id_contrato }}</b>
                          <svg class="doc-link-arrow" width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="7" y1="17" x2="17" y2="7"></line><polyline points="7 7 17 7 17 17"></polyline></svg>
                        </span>
                      </div>

                      <!-- Linha Financeira: Valor e Vencimento -->
                      <div class="cobranca-financeiro-row">
                        <div class="cobranca-valor-container">
                          <span class="cobranca-valor-label">VALOR</span>
                          <span class="cobranca-valor-numero text-rose-700">{{ $currency(data.valor) }}</span>
                        </div>
                        <div class="cobranca-vencimento-container">
                          <span class="cobranca-vencimento-label">VENCIMENTO</span>
                          <span class="cobranca-vencimento-data">
                            <AppIcon name="calendar" size="12" />
                            {{ data.vencimento }}
                          </span>
                        </div>
                      </div>

                      <!-- Observação / Motivo da Falha -->
                      <div v-if="data.motivo" class="cobranca-obs-row obs-falha" title="Motivo do não sucesso da cobrança">
                        <span class="cobranca-obs-label">Motivo:</span>
                        <span class="cobranca-obs-text">{{ data.motivo }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </template>
            </div>
          </perfect-scrollbar>
        </div>
      </vs-col>
    </vs-row>
    <vx-card class="mt-1" no-shadow card-background="dark">
      <vs-row
        vs-type="flex"
        class
        vs-justify="flex-start"
        vs-align="flex-start"
        vs-w="12"
      >
        <vs-col
          vs-type="flex"
          vs-justify="flex-start"
          vs-align="flex-start"
          vs-w="1"
          class
        >
          <vs-button
            @click="voltar()"
            icon-pack="feather"
            line-origin="left"
            color="#fff"
            type="line"
            icon="icon-arrow-left"
            size="small"
          >
            <span style="font-size: 14px">Voltar</span>
          </vs-button>
        </vs-col>
      </vs-row>
    </vx-card>
    <vs-popup
      :title="'COBRAR CONTA N° ' + contaSelecionada.id"
      :active.sync="iniciarModal"
    >
      <vs-row vs-type="flex" vs-align="center">
        <vs-col
          vs-w="4"
          vs-type="flex"
          vs-justify="center"
          vs-align="center"
          class="p-5"
        >
          <Confirmar @update="close()" :item="contaSelecionada"></Confirmar>
        </vs-col>
        <vs-col
          vs-w="4"
          vs-type="flex"
          vs-justify="center"
          vs-align="center"
          class="p-5"
        >
          <Declinar @update="close()" :item="contaSelecionada"></Declinar>
        </vs-col>
        <vs-col
          vs-w="4"
          vs-type="flex"
          vs-justify="center"
          vs-align="center"
          class="p-5"
        >
          <Contactar 
            @update="close()" 
            @confirmacao-atualizada="atualizarContatoConfirmacaoLocal"
            @contatos-atualizados="getContasParaCobrar()"
            @fechar-pai="iniciarModal = false"
            :item="contaSelecionada"
          ></Contactar>
        </vs-col>
      </vs-row>
    </vs-popup>

    <modal-disparo-em-massa-cobranca
      :active.sync="modalMassaActive"
      :contasList="paraCobrar"
      @success="fecharModalMassaComSucesso"
    />
  </div>
</template>
<script>
import components from "@/components/default/exports.js";
import Confirmar from "./components/confirmar";
import Declinar from "./components/declinar";
import Contactar from "./components/contactar";
import VisualizarCliente from "./../../../cliente/visualizar.vue";
import ModalDisparoEmMassaCobranca from "./components/modalDisparoEmMassaCobranca.vue";
import { 
  getFormaPagamentoBadge, 
  getFormaPagamentoMeta 
} from "@/constants/cobrancaCenario.js";
export default {
  data() {
    return {
      tipo_pessoa: [
        { value: 1, nome: "PESSOA FÍSICA" },
        { value: 2, nome: "PESSOA JURÍDICA" },
      ],
      status_conta_opcoes: [
        { value: 'todos', nome: "TODAS AS PENDÊNCIAS" },
        { value: 'pendente', nome: "APENAS 100% PENDENTES" },
        { value: 'quitacao_parcial', nome: "APENAS QUITAÇÃO PARCIAL" },
      ],
      paraCobrar: [],
      paraCobrarLista: [],
      paraCobrarCount: 2,
      loadingParaCobrar: false,

      sucedidos: [],
      sucedidosLista: [],
      sucedidosCount: 2,
      loadingSucedidos: false,

      naoSucedidos: [],
      naoSucedidosLista: [],
      naoSucedidosCount: 2,
      loadingNaoSucedidos: false,

      empresas: [],
      modalMassaActive: false,
      ultimoJob: null,

      settings: {
        maxScrollbarLength: 60,
        wheelSpeed: 0.8,
      },
      filtro: {
        dataDe: (() => {
          const d = new Date();
          d.setDate(d.getDate() - 1);
          const ano = d.getFullYear();
          const mes = String(d.getMonth() + 1).padStart(2, '0');
          const dia = String(d.getDate()).padStart(2, '0');
          return `${ano}-${mes}-${dia}`;
        })(),
        dataAte: (() => {
          const d = new Date();
          d.setDate(d.getDate() - 1);
          const ano = d.getFullYear();
          const mes = String(d.getMonth() + 1).padStart(2, '0');
          const dia = String(d.getDate()).padStart(2, '0');
          return `${ano}-${mes}-${dia}`;
        })(),
        dataHoje: this.$formartData.dataFiltro(),
        periodo: 'ontem',
        id_empresa: null,
        tipo_pessoa: null,
        forma_pagamento: null,
        status_conta: 'todos',
      },
      cardClass: "cobranca-card overflow-hidden",
      contaSelecionada: {},
      iniciarModal: false,

      formasPagamento: null,
    };
  },
  watch: {
    '$route.query': {
      handler(newQuery) {
        if (!newQuery) return;
        this.carregarFiltrosDaUrl();
      }
    }
  },
  methods: {
    async voltar() {
      this.$router.push("/financeiro");
    },
    async getContasParaCobrar() {
      this.loadingParaCobrar = true;
      try {
        this.paraCobrar = await this.$http.post(
          `getContasParaCobrar`,
          this.filtro
        );

        this.paraCobrar.forEach((conta) => {
          if (conta.dias_atraso != null) {
            conta.diasVencidos = Number(conta.dias_atraso);
          } else if (conta.data_vencimento) {
            const parts = String(conta.data_vencimento).split('T')[0].split('-');
            if (parts.length === 3) {
              const ano = parseInt(parts[0], 10);
              const mes = parseInt(parts[1], 10) - 1;
              const dia = parseInt(parts[2], 10);
              const dataVenc = new Date(ano, mes, dia);
              const agora = new Date();
              const hojeDate = new Date(agora.getFullYear(), agora.getMonth(), agora.getDate());
              conta.diasVencidos = Math.round((hojeDate.getTime() - dataVenc.getTime()) / 86400000);
            } else {
              conta.diasVencidos = 0;
            }
          } else {
            conta.diasVencidos = 0;
          }

          if (conta.id_status == 1 || !conta.id_status) {
            conta.badge = "success";
          } else if (conta.id_status == 2) {
            conta.badge = "danger";
          } else {
            conta.badge = "warning";
          }
          return conta;
        });
        this.paraCobrarLista = this.paraCobrar.slice(0, 10);
      } catch (err) {
        const error = this.$httpErrors(err);
        this.$vs.notify(error);
        if(error.code == 401) {
         await window.localStorage.clear();
         await this.$router.push("/"); 
        }
      } finally {
        this.loadingParaCobrar = false;
      }
    },
    async getContasCobradas() {
      this.loadingSucedidos = true;
      try {
        this.sucedidos = await this.$http.post(
          `getContasCobradas`,
          this.filtro
        );
        this.sucedidosLista = this.sucedidos.slice(0, 10);
      } catch (err) {
        const error = this.$httpErrors(err);
        this.$vs.notify(error);
        if(error.code == 401) {
         await window.localStorage.clear();
         await this.$router.push("/"); 
        }
      } finally {
        this.loadingSucedidos = false;
      }
    },
    async getContasNaoSucedidas() {
      this.loadingNaoSucedidos = true;
      try {
        this.naoSucedidos = await this.$http.post(
          `getContasNaoSucedidas`,
          this.filtro
        );
        this.naoSucedidosLista = this.naoSucedidos.slice(0, 10);
      } catch (err) {
        const error = this.$httpErrors(err);
        this.$vs.notify(error);
        if(error.code == 401) {
         await window.localStorage.clear();
         await this.$router.push("/"); 
        }
      } finally {
        this.loadingNaoSucedidos = false;
      }
    },
    async showMoreCobrancaParaCobrar() {
      if (
        this.paraCobrar.length > 10 &&
        this.paraCobrar.length > this.paraCobrarLista.length
      ) {
        this.$vs.loading();
        await setTimeout(() => {
          this.paraCobrarLista = this.paraCobrar.slice(
            0,
            10 * this.paraCobrarCount
          );
          this.paraCobrarCount += 1;
          this.$vs.loading.close();
        }, 200);
      }
    },
    async showMoreCobrancaSucedidos() {
      if (
        this.sucedidos.length > 10 &&
        this.sucedidos.length > this.sucedidosLista.length
      ) {
        this.$vs.loading();
        await setTimeout(() => {
          this.sucedidosLista = this.sucedidos.slice(
            0,
            10 * this.sucedidosCount
          );
          this.sucedidosCount += 1;
          this.$vs.loading.close();
        }, 200);
      }
    },
    async showMoreCobrancaNaoSucedidos() {
      if (
        this.naoSucedidos.length > 10 &&
        this.naoSucedidos.length > this.naoSucedidosLista.length
      ) {
        this.$vs.loading();
        await setTimeout(() => {
          this.naoSucedidosLista = this.naoSucedidos.slice(
            0,
            10 * this.naoSucedidosCount
          );
          this.naoSucedidosCount += 1;
          this.$vs.loading.close();
        }, 200);
      }
    },

    carregarFiltrosDaUrl() {
      const q = this.$route ? this.$route.query : null;
      if (!q) return;
      if (q.dataDe) this.filtro.dataDe = q.dataDe;
      if (q.dataAte) this.filtro.dataAte = q.dataAte;
      if (q.periodo) this.filtro.periodo = q.periodo;

      const tipoPessoaVal = q.tipo_pessoa !== undefined && q.tipo_pessoa !== null && q.tipo_pessoa !== '' ? q.tipo_pessoa : (q.tipoPessoa !== undefined && q.tipoPessoa !== null && q.tipoPessoa !== '' ? q.tipoPessoa : null);
      if (tipoPessoaVal !== null) {
        this.filtro.tipo_pessoa = Number(tipoPessoaVal);
      } else {
        this.filtro.tipo_pessoa = null;
      }

      const formaVal = q.forma_pagamento !== undefined && q.forma_pagamento !== null && q.forma_pagamento !== '' ? q.forma_pagamento : (q.id_forma_pagamento !== undefined && q.id_forma_pagamento !== null && q.id_forma_pagamento !== '' ? q.id_forma_pagamento : (q.formaPagamento !== undefined && q.formaPagamento !== null && q.formaPagamento !== '' ? q.formaPagamento : null));
      if (formaVal !== null) {
        this.filtro.forma_pagamento = Number(formaVal);
        this.filtro.id_forma_pagamento = Number(formaVal);
      } else {
        this.filtro.forma_pagamento = null;
        this.filtro.id_forma_pagamento = null;
      }

      const empresaVal = q.id_empresa !== undefined && q.id_empresa !== null && q.id_empresa !== '' ? q.id_empresa : (q.empresa !== undefined && q.empresa !== null && q.empresa !== '' ? q.empresa : null);
      if (empresaVal !== null) {
        this.filtro.id_empresa = Number(empresaVal);
        this.filtro.empresa = Number(empresaVal);
      } else {
        this.filtro.id_empresa = null;
        this.filtro.empresa = null;
      }

      if (q.status_conta !== undefined && q.status_conta !== null && q.status_conta !== '') {
        this.filtro.status_conta = String(q.status_conta);
      } else {
        this.filtro.status_conta = 'todos';
      }
    },

    sincronizarFiltrosComUrl() {
      if (!this.$router || !this.$route) return;
      const query = {};
      if (this.filtro.dataDe) query.dataDe = this.filtro.dataDe;
      if (this.filtro.dataAte) query.dataAte = this.filtro.dataAte;
      if (this.filtro.periodo) query.periodo = this.filtro.periodo;
      if (this.filtro.tipo_pessoa !== null && this.filtro.tipo_pessoa !== undefined && this.filtro.tipo_pessoa !== '') {
        query.tipo_pessoa = String(this.filtro.tipo_pessoa);
      }
      const forma = this.filtro.forma_pagamento !== null && this.filtro.forma_pagamento !== undefined && this.filtro.forma_pagamento !== '' ? this.filtro.forma_pagamento : (this.filtro.id_forma_pagamento !== null && this.filtro.id_forma_pagamento !== undefined && this.filtro.id_forma_pagamento !== '' ? this.filtro.id_forma_pagamento : null);
      if (forma !== null) {
        query.forma_pagamento = String(forma);
      }
      const emp = this.filtro.id_empresa !== null && this.filtro.id_empresa !== undefined && this.filtro.id_empresa !== '' ? this.filtro.id_empresa : (this.filtro.empresa !== null && this.filtro.empresa !== undefined && this.filtro.empresa !== '' ? this.filtro.empresa : null);
      if (emp !== null) {
        query.id_empresa = String(emp);
      }
      if (this.filtro.status_conta && this.filtro.status_conta !== 'todos') {
        query.status_conta = String(this.filtro.status_conta);
      }

      const currentQuery = this.$route.query || {};
      const currentKeys = Object.keys(currentQuery);
      const newKeys = Object.keys(query);
      let isDifferent = currentKeys.length !== newKeys.length;
      if (!isDifferent) {
        for (let i = 0; i < newKeys.length; i++) {
          const k = newKeys[i];
          if (String(currentQuery[k]) !== String(query[k])) {
            isDifferent = true;
            break;
          }
        }
      }

      if (isDifferent) {
        this.$router.push({ query }).catch(() => {});
      }
    },

    async onPeriodoChange(payload) {
      if (!payload) return;
      this.filtro.dataDe = payload.dataDe;
      this.filtro.dataAte = payload.dataAte;
      this.filtro.periodo = payload.periodo;
      this.sincronizarFiltrosComUrl();
      await this.close();
    },

    async onFiltroChange() {
      this.filtro.id_forma_pagamento = this.filtro.forma_pagamento;
      this.filtro.empresa = this.filtro.id_empresa;
      this.sincronizarFiltrosComUrl();
      await this.close();
    },

    onDataInputChange() {
      this.filtro.periodo = null;
      this.sincronizarFiltrosComUrl();
    },

    abrirLinkNota(data) {
      if (!data) return;
      const query = {};
      if (data.id_contrato) query.contrato = data.id_contrato;
      if (data.id_cliente) query.cliente = data.id_cliente;
      const routeData = this.$router.resolve({
        path: '/nota',
        query
      });
      window.open(routeData.href, '_blank');
    },

    abrirLinkConta(data) {
      if (!data) return;
      const query = {};
      if (data.id_contrato) query.contrato = data.id_contrato;
      if (data.id_cliente) query.cliente = data.id_cliente;
      if (data.id) query.id = data.id;
      const routeData = this.$router.resolve({
        path: '/contas-receber',
        query
      });
      window.open(routeData.href, '_blank');
    },

    abrirLinkContrato(data) {
      if (!data || !data.id_contrato) return;
      const routeData = this.$router.resolve({
        path: '/contrato',
        query: { id: data.id_contrato }
      });
      window.open(routeData.href, '_blank');
    },

    async close() {
      this.sincronizarFiltrosComUrl();
      this.iniciarModal = false;
      await Promise.all([
        this.getContasParaCobrar(),
        this.getContasCobradas(),
        this.getContasNaoSucedidas()
      ]);
    },

    async getFormasPagamento(){
      try{
        this.formasPagamento = await this.$http.get("formasPagamento")
      }catch(error){
        const erro = this.$httpErrors(error)
        this.$vs.notify(erro)
      }
    },

    async getEmpresas() {
      try {
        this.empresas = await this.$http.get("empresa");
      } catch (err) {
        console.error(err);
      }
    },

    async getUltimoJobStatus() {
      try {
        const res = await this.$http.get('whatsapp/enviar-massa/ultimo-job/cobranca');
        const responseData = res.data || res;
        this.ultimoJob = responseData.data !== undefined ? responseData.data : responseData;
      } catch (err) {
        console.error("Erro ao buscar histórico do disparo em massa:", err);
      }
    },

    formatarDataJob(dataIso) {
      if (!dataIso) return '';
      const data = new Date(dataIso);
      return data.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
    },

    abrirReguaDisparos() {
      const routeData = this.$router.resolve({
        path: '/regua-disparos',
        query: { tab: 'cobranca' }
      });
      window.open(routeData.href, '_blank');
    },

    abrirModalMassa() {
      this.modalMassaActive = true;
    },

    async fecharModalMassaComSucesso() {
      this.modalMassaActive = false;
      await this.getDadosDashboard();
    },

    async getDadosDashboard() {
      await Promise.all([
        this.getContasParaCobrar(),
        this.getContasCobradas(),
        this.getContasNaoSucedidas(),
        this.getUltimoJobStatus()
      ]);
    },

    atualizarContatoConfirmacaoLocal({ idContrato, idContatoConfirmacao, nomeContatoConfirmacao, contatoConfirmacao }) {
      this.atualizarContatoConfirmacaoNaLista(
        this.paraCobrar,
        idContrato,
        idContatoConfirmacao,
        nomeContatoConfirmacao,
        contatoConfirmacao
      );
      this.atualizarContatoConfirmacaoNaLista(
        this.paraCobrarLista,
        idContrato,
        idContatoConfirmacao,
        nomeContatoConfirmacao,
        contatoConfirmacao
      );
      this.atualizarContatoConfirmacaoNaLista(
        this.sucedidos,
        idContrato,
        idContatoConfirmacao,
        nomeContatoConfirmacao,
        contatoConfirmacao
      );
      this.atualizarContatoConfirmacaoNaLista(
        this.sucedidosLista,
        idContrato,
        idContatoConfirmacao,
        nomeContatoConfirmacao,
        contatoConfirmacao
      );
      this.atualizarContatoConfirmacaoNaLista(
        this.naoSucedidos,
        idContrato,
        idContatoConfirmacao,
        nomeContatoConfirmacao,
        contatoConfirmacao
      );
      this.atualizarContatoConfirmacaoNaLista(
        this.naoSucedidosLista,
        idContrato,
        idContatoConfirmacao,
        nomeContatoConfirmacao,
        contatoConfirmacao
      );

      if (this.contaSelecionada && this.contaSelecionada.id_contrato == idContrato) {
        this.contaSelecionada.id_contato_confirmacao = idContatoConfirmacao;
        if (nomeContatoConfirmacao !== undefined) {
          this.contaSelecionada.nomeContatoConfirmacao = nomeContatoConfirmacao;
        }
        if (contatoConfirmacao !== undefined) {
          this.contaSelecionada.contatoConfirmacao = contatoConfirmacao;
        }
      }
    },

    atualizarContatoConfirmacaoNaLista(
      lista,
      idContrato,
      idContatoConfirmacao,
      nomeContatoConfirmacao,
      contatoConfirmacao
    ) {
      if (!Array.isArray(lista)) {
        return;
      }
      lista.forEach((item) => {
        if (item.id_contrato == idContrato) {
          item.id_contato_confirmacao = idContatoConfirmacao;
          if (nomeContatoConfirmacao !== undefined) {
            item.nomeContatoConfirmacao = nomeContatoConfirmacao;
          }
          if (contatoConfirmacao !== undefined) {
            item.contatoConfirmacao = contatoConfirmacao;
          }
        }
      });
    },

    getStatusBadge(data) {
      if (!data) return { text: 'Pendente', class: 'badge-pendente', tooltip: '' };
      if (data.id_status === 3) {
        return { text: 'Quit. Parcial', class: 'badge-pendente', tooltip: 'Fatura com quitação parcial registrada no financeiro.' };
      }
      const dias = data.dias_atraso != null ? Number(data.dias_atraso) : (data.diasVencidos != null ? Number(data.diasVencidos) : 0);
      if (dias > 0) {
        return {
          text: data.status || 'Vencido',
          class: 'badge-vencido',
          tooltip: `Fatura Vencida (${dias} ${dias === 1 ? 'dia' : 'dias'} de atraso). Conta pendente de regularização.`
        };
      }
      if (dias === 0) {
        return {
          text: data.status || 'Pendente',
          class: 'badge-pendente',
          tooltip: 'Fatura com vencimento na data de hoje. Status no financeiro: ' + (data.status || 'Pendente') + '.'
        };
      }
      const diasFaltando = Math.abs(dias);
      return {
        text: 'A Vencer',
        class: 'badge-a-vencer',
        tooltip: `Fatura a vencer em ${diasFaltando} ${diasFaltando === 1 ? 'dia' : 'dias'} (${data.vencimento || ''}). Dentro do prazo de pagamento.`
      };
    },

    getDiasVencimentoBadge(data) {
      if (!data) return { text: 'Vence Hoje', class: 'badge-dias-zero', tooltip: '' };
      const dias = data.dias_atraso != null ? Number(data.dias_atraso) : (data.diasVencidos != null ? Number(data.diasVencidos) : 0);
      if (dias > 0) {
        return {
          text: `${dias}d de atraso`,
          class: 'badge-dias-vencidos',
          tooltip: `Fatura vencida há ${dias} ${dias === 1 ? 'dia' : 'dias'}. Conta pendente de liquidação.`
        };
      }
      if (dias === 0) {
        return {
          text: 'Vence Hoje',
          class: 'badge-dias-zero',
          tooltip: 'Data de vencimento é hoje. Fatura pronta para cobrança/lembrete no dia.'
        };
      }
      const diasFaltando = Math.abs(dias);
      return {
        text: `Vence em ${diasFaltando}d`,
        class: 'badge-dias-a-vencer',
        tooltip: `Fatura a vencer em ${diasFaltando} ${diasFaltando === 1 ? 'dia' : 'dias'} (${data.vencimento || ''}). Dentro do prazo de pagamento.`
      };
    },

    getFormaPagamentoBadge(item) {
      return getFormaPagamentoBadge(item);
    },

    getFormaPagamentoMeta(nomeForma) {
      return getFormaPagamentoMeta(nomeForma);
    }
  },
  created() {
    this.carregarFiltrosDaUrl();
  },
  async mounted() {
    this.carregarFiltrosDaUrl();
    await Promise.all([
      this.getFormasPagamento(),
      this.getEmpresas(),
      this.getContasParaCobrar(),
      this.getContasCobradas(),
      this.getContasNaoSucedidas(),
      this.getUltimoJobStatus()
    ]);
  },
  components: {
    ...components,
    VisualizarCliente,
    Declinar,
    Contactar,
    Confirmar,
    ModalDisparoEmMassaCobranca,
  },
};
</script>
<style scoped lang="scss">
.border {
  border-width: 1px;
  border-color: #ebebeb;
  border-radius: 1%;
}
.cobranca-card {
  transition-duration: 50ms;
  &:hover {
    transition-duration: 150ms;
    transform: translateY(-3px);
    box-shadow: 0px 4px 25px 0px rgba(0, 0, 0, 0.25);
    background-color: #eeeeee;
    color: black;
    cursor: pointer;

    .grid-view-img {
      opacity: 0.9;
    }
  }
}
.scroll-cobranca {
  height: 70vh !important;
}

/* Apple Design Badges */
.apple-card-badge {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 2px 6px;
  border-radius: 6px;
  font-size: 10.5px;
  font-weight: 600;
  line-height: 1.2;
  letter-spacing: -0.01em;
  transition: all 0.15s ease;
}

.badge-svg-icon {
  width: 12px;
  height: 12px;
  flex-shrink: 0;
}

.badge-pendente {
  background: #f1f5f9;
  border: 1px solid #cbd5e1;
  color: #475569;
}

.badge-vencido {
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #b91c1c;
}

.badge-apple-renegociacao {
  background: #fff7ed;
  border: 1px solid #fed7aa;
  color: #c2410c;
  font-weight: 700;
}

.badge-apple-pix {
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  color: #15803d;
}

.badge-apple-boleto {
  background: #f0f9ff;
  border: 1px solid #bae6fd;
  color: #0369a1;
}

.badge-apple-cartao {
  background: #faf5ff;
  border: 1px solid #e9d5ff;
  color: #7e22ce;
}

.badge-apple-dinheiro {
  background: #ecfdf5;
  border: 1px solid #a7f3d0;
  color: #047857;
}

.badge-apple-transferencia {
  background: #eef2ff;
  border: 1px solid #c7d2fe;
  color: #4338ca;
}

.badge-apple-empenho {
  background: #ecfeff;
  border: 1px solid #a5f3fc;
  color: #0e7490;
}

.badge-apple-permuta {
  background: #fffbeb;
  border: 1px solid #fde68a;
  color: #b45309;
}

.badge-apple-outro-contrato {
  background: #f5f3ff;
  border: 1px solid #ddd6fe;
  color: #6d28d9;
}

.badge-apple-outro {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  color: #475569;
}

.badge-dias-zero {
  background: #fffbeb;
  border: 1px solid #fde68a;
  color: #b45309;
  font-weight: 600;
}

.badge-dias-vencidos {
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #dc2626;
  font-weight: 700;
}

.badge-dias-a-vencer {
  background: #f0f9ff;
  border: 1px solid #bae6fd;
  color: #0284c7;
  font-weight: 600;
}

.badge-a-vencer {
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  color: #15803d;
  font-weight: 600;
}

.badge-pago {
  background: #ecfdf5;
  border: 1px solid #a7f3d0;
  color: #047857;
  font-weight: 600;
}

.badge-falha {
  background: #fff1f2;
  border: 1px solid #fecdd3;
  color: #e11d48;
  font-weight: 600;
}

.dropdown-icon-wrapper {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 4px;
  background: rgba(0, 0, 0, 0.04);
}

/* Layout e Toolbar de Cobrança Responsiva */
.cobranca-page-wrapper {
  width: 100%;
}

.cobranca-top-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
  flex-wrap: nowrap;
}

.cobranca-title-container {
  display: flex;
  align-items: center;
  align-self: center;
  margin-left: 4px;
  margin-right: 8px;
  flex-shrink: 0;
}

.cobranca-page-heading {
  color: #000000;
  font-size: 1.35rem;
  font-weight: 800;
  margin: 0;
  letter-spacing: -0.02em;
  line-height: 1;
}

.cobranca-filters-container {
  display: flex;
  align-items: flex-end;
  justify-content: flex-end;
  flex-wrap: nowrap;
  gap: 6px;
  flex: 1;
}

@media (max-width: 1200px) {
  .cobranca-top-toolbar {
    flex-direction: column;
    align-items: flex-start;
  }
  .cobranca-filters-container {
    justify-content: flex-start;
    width: 100%;
    flex-wrap: wrap;
  }
}

/* Toolbar de Filtros Apple Design */
.filtro-toolbar-item {
  display: inline-flex;
  flex-direction: column;
  justify-content: flex-end;
  margin: 0;
}

.filtro-toolbar-label {
  color: #1e293b;
  font-size: 0.82rem;
  font-weight: 600;
  letter-spacing: -0.01em;
  margin: 0 0 6px 2px;
  line-height: 1.2;
  height: 16px;
  white-space: nowrap;
}

.filtro-toolbar-btn-wrapper {
  display: inline-flex;
  flex-direction: column;
  justify-content: flex-end;
  margin: 0;
}

.filtro-btn-action {
  height: 34px;
  min-height: 34px;
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 0 14px;
  background: #f0fdf4;
  color: #00bba2;
  border: 1px solid #00bba2;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  cursor: pointer;
  outline: none;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  transition: all 0.2s cubic-bezier(0.16, 1, 0.3, 1);

  &:hover {
    background: #00bba2;
    color: #ffffff;
    border-color: #00bba2;
    transform: translateY(-1px);
    box-shadow: 0 4px 10px rgba(0, 187, 162, 0.25);
  }

  &:active {
    transform: translateY(0) scale(0.98);
  }
}

/* Kanban Headers Modernos e Refinados */
.cobranca-kanban-column {
  width: 100%;
}

.cobranca-column-card {
  border-radius: 10px 10px 0 0;
  padding: 12px 14px;
  min-height: 78px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  box-shadow: 0 4px 12px -2px rgba(0, 0, 0, 0.08);

  &.card-header-pendentes {
    background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
    border: 1px solid #334155;
    border-bottom: none;
    .cobranca-col-icon { color: #38bdf8; }
    .cobranca-col-count { background: rgba(255, 255, 255, 0.15); color: #ffffff; border: 1px solid rgba(255, 255, 255, 0.2); }
  }

  &.card-header-sucesso {
    background: linear-gradient(135deg, #064e3b 0%, #047857 100%);
    border: 1px solid #059669;
    border-bottom: none;
    .cobranca-col-icon { color: #6ee7b7; }
    .cobranca-col-count { background: rgba(255, 255, 255, 0.2); color: #ffffff; border: 1px solid rgba(255, 255, 255, 0.25); }
  }

  &.card-header-falhas {
    background: linear-gradient(135deg, #881337 0%, #be123c 100%);
    border: 1px solid #e11d48;
    border-bottom: none;
    .cobranca-col-icon { color: #fecdd3; }
    .cobranca-col-count { background: rgba(255, 255, 255, 0.2); color: #ffffff; border: 1px solid rgba(255, 255, 255, 0.25); }
  }
}

.cobranca-col-top-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.cobranca-col-title-group {
  display: flex;
  align-items: center;
  gap: 8px;
}

.cobranca-col-icon {
  display: flex;
  align-items: center;
  justify-content: center;
}

.cobranca-col-name {
  font-size: 13.5px;
  font-weight: 700;
  color: #ffffff;
  letter-spacing: -0.01em;
}

.cobranca-col-count {
  font-family: SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 12px;
  font-weight: 800;
  padding: 2px 9px;
  border-radius: 100px;
  line-height: 1.2;
}

.cobranca-col-bottom-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-top: 8px;
}

.cobranca-col-meta {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 11.5px;
  font-weight: 500;
  color: #f8fafc;
  letter-spacing: 0.01em;

  svg, .app-icon-wrapper {
    color: #38bdf8;
    opacity: 0.95;
  }
}

.cobranca-col-status-text {
  font-size: 11.5px;
  color: #f8fafc;
  font-weight: 500;
  letter-spacing: 0.01em;
}

.cobranca-btn-disparo {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 5px 12px;
  background: linear-gradient(135deg, #00bba2 0%, #009688 100%);
  color: #ffffff;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 6px;
  font-size: 11px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 2px 6px rgba(0, 187, 162, 0.3);
  transition: all 0.2s cubic-bezier(0.16, 1, 0.3, 1);

  &:hover:not(:disabled) {
    background: linear-gradient(135deg, #00d6ba 0%, #00bba2 100%);
    transform: translateY(-1px);
    box-shadow: 0 4px 10px rgba(0, 187, 162, 0.4);
  }

  &:active:not(:disabled) {
    transform: translateY(0) scale(0.98);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

/* Apple Design Cards */
.cobranca-apple-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 12px 14px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.03);
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.cobranca-apple-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px -4px rgba(0, 0, 0, 0.08), 0 4px 6px -2px rgba(0, 0, 0, 0.02);
  border-color: #cbd5e1;
  background: #fafbfc;
}

.cobranca-apple-card.card-vencido {
  border-left: 3.5px solid #f87171;
}

.cobranca-apple-card.card-sucedido {
  border-left: 3.5px solid #10b981;
}

.cobranca-apple-card.card-nao-sucedido {
  border-left: 3.5px solid #f43f5e;
}

.cobranca-card-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
}

.cobranca-cliente-wrapper {
  flex: 1;
  min-width: 140px;
}

.cobranca-cliente-wrapper ::v-deep .vs-button {
  text-align: left;
}

.cobranca-cliente-wrapper ::v-deep .nome-cliente-medium,
.cobranca-cliente-wrapper ::v-deep .nome-cliente-small {
  font-weight: 700;
  color: #0f172a;
  font-size: 13.5px;
  line-height: 1.3;
  transition: color 0.15s ease;
}

.cobranca-cliente-wrapper ::v-deep .vs-button:hover span {
  color: #0284c7;
  text-decoration: underline;
}

.cobranca-badges-group {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  flex-wrap: wrap;
  justify-content: flex-end;
  max-width: 55%;
}

.cobranca-card-body {
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-top: 6px;
  border-top: 1px dashed #f1f5f9;
}

.cobranca-docs-row {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}

.cobranca-doc-pill {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-size: 11px;
  color: #475569;
  background: #f8fafc;
  padding: 2px 7px;
  border-radius: 6px;
  font-weight: 500;
  border: 1px solid #e2e8f0;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  user-select: none;

  &:hover {
    background: #f1f5f9;
    color: #0284c7;
    border-color: #cbd5e1;
    transform: translateY(-1px);
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);

    b {
      color: #0284c7;
    }

    .doc-link-arrow {
      opacity: 1;
      max-width: 14px;
      margin-left: 2px;
      transform: translateX(0);
      color: #0284c7;
    }
  }
}

.doc-link-arrow {
  opacity: 0;
  max-width: 0;
  margin-left: 0;
  transform: translateX(-4px);
  color: #64748b;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;
  flex-shrink: 0;
  display: inline-block;
  vertical-align: middle;
}

.cobranca-doc-pill b {
  color: #0f172a;
  font-weight: 600;
  transition: color 0.15s ease;
}

.cobranca-doc-pill.doc-contrato {
  background: #f1f5f9;

  &:hover {
    background: #e0f2fe;
    color: #0369a1;
    border-color: #bae6fd;

    b {
      color: #0369a1;
    }

    .doc-link-arrow {
      color: #0369a1;
    }
  }
}

.cobranca-financeiro-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding-top: 2px;
}

.cobranca-valor-container {
  display: flex;
  flex-direction: column;
}

.cobranca-valor-label {
  font-size: 9.5px;
  font-weight: 700;
  color: #94a3b8;
  letter-spacing: 0.5px;
  text-transform: uppercase;
}

.cobranca-valor-numero {
  font-size: 14.5px;
  font-weight: 800;
  color: #0f172a;
  line-height: 1.2;
}

.cobranca-vencimento-container {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}

.cobranca-vencimento-label {
  font-size: 9.5px;
  font-weight: 700;
  color: #94a3b8;
  letter-spacing: 0.5px;
  text-transform: uppercase;
}

.cobranca-vencimento-data {
  font-size: 12px;
  font-weight: 600;
  color: #475569;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  line-height: 1.2;
}

.cobranca-obs-row {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  font-size: 11.5px;
  line-height: 1.4;
  padding: 6px 9px;
  margin-top: 8px;
  background: #f8fafc;
  border-radius: 6px;
  border: 1px solid #e2e8f0;
}

.cobranca-obs-row.obs-falha {
  background: #fff1f2;
  border-color: #fecdd3;
}

.cobranca-obs-label {
  font-weight: 700;
  color: #475569;
  font-size: 11.5px;
  line-height: 1.4;
  flex-shrink: 0;
}

.cobranca-obs-row.obs-falha .cobranca-obs-label {
  color: #e11d48;
}

.cobranca-obs-text {
  color: #334155;
  font-size: 11.5px;
  line-height: 1.4;
  flex: 1;
  word-break: break-word;
}

.cobranca-obs-row.obs-falha .cobranca-obs-text {
  color: #881337;
}

/* =========================================================
   Skeleton Loading Shimmer
========================================================= */
.cobranca-skeleton-list {
  padding: 4px 8px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.cobranca-skeleton-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.03);
}

.cobranca-skeleton-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.cobranca-skeleton-docs {
  display: flex;
  gap: 6px;
}

.cobranca-skeleton-fin {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 4px;
  border-top: 1px dashed #f1f5f9;
}

.skeleton-shimmer {
  background: linear-gradient(90deg, #f1f5f9 0%, #e2e8f0 50%, #f1f5f9 100%);
  background-size: 200% 100%;
  animation: cobrancaShimmerAnimation 1.5s infinite linear;
  border-radius: 6px;
}

@keyframes cobrancaShimmerAnimation {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

.skeleton-title-bar {
  width: 55%;
  height: 16px;
  border-radius: 4px;
}

.skeleton-badge-pill {
  width: 70px;
  height: 18px;
  border-radius: 100px;
}

.skeleton-doc-pill {
  width: 75px;
  height: 20px;
  border-radius: 6px;
}

.skeleton-val-box {
  width: 85px;
  height: 24px;
  border-radius: 6px;
}

/* =========================================================
   Empty State Cards
========================================================= */
.cobranca-empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 36px 20px;
  margin: 12px 8px;
  background: #ffffff;
  border: 1px dashed #cbd5e1;
  border-radius: 12px;
  min-height: 200px;
}

.empty-state-icon-circle {
  width: 52px;
  height: 52px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 12px;
}

.empty-state-icon-circle.icon-circle-pendentes {
  background: #f0fdf4;
  color: #00bba2;
  border: 1px solid #bbf7d0;
}

.empty-state-icon-circle.icon-circle-sucesso {
  background: #f0f9ff;
  color: #0284c7;
  border: 1px solid #bae6fd;
}

.empty-state-icon-circle.icon-circle-falhas {
  background: #f0fdf4;
  color: #10b981;
  border: 1px solid #bbf7d0;
}

.empty-state-title {
  font-size: 14px;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 6px 0;
  letter-spacing: -0.01em;
}

.empty-state-desc {
  font-size: 12px;
  color: #64748b;
  max-width: 260px;
  margin: 0;
  line-height: 1.45;
}
</style>
